import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { offsetLabel } from '@shared/lib/i18n/formatters';
import { listTimeZones } from '@shared/lib/timezones';
import { browserTimeZone } from '@shared/model/preferences';
import { SelectField } from './Field';

export interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string | undefined;
  required?: boolean;
  /** Show "Detected from this device" hint when the value equals the browser zone. */
  showDetectedHint?: boolean;
}

/** IANA zone picker with offsets; defaults to the zone auto-detected from the browser. */
export function TimezoneSelect({ value, onChange, label, error, required, showDetectedHint = true }: TimezoneSelectProps) {
  const { t } = useTranslation('common');
  const detected = browserTimeZone();
  const options = useMemo(() => {
    const now = new Date();
    return listTimeZones().map((zone) => ({ value: zone, label: `${zone.replace(/_/g, ' ')} (${offsetLabel(zone, now)})` }));
  }, []);
  const hint = showDetectedHint
    ? value === detected
      ? t('timezone.detected')
      : t('timezone.differs', { zone: detected.replace(/_/g, ' ') })
    : undefined;
  return (
    <SelectField
      label={label ?? t('timezone.label')}
      hint={hint}
      error={error}
      required={required}
      options={options}
      value={value}
      dir="ltr"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
